import { SearchOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { Empty, Input, Spin, theme } from 'antd';
import type React from 'react';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';
import type {
  ParameterSelection_ProfileFragment,
  ParameterSelection_RequestableParameterFragment,
  ParameterToAddInput,
} from '../../../graphql/generated/graphql.ts';
import type { SelectedParameter } from '../../../hooks/store/useAppStore.ts';
import { deCollator } from '../../../utils/collator.ts';
import { MemoProfileCard } from './ProfileCard.tsx';

type ProfilesProps = {
  params: readonly ParameterSelection_RequestableParameterFragment[];
  requestableByMainId: Map<string, ParameterSelection_RequestableParameterFragment>;
  profiles: readonly ParameterSelection_ProfileFragment[];
  selectedParams: SelectedParameter[];
  selectedProfileIds: string[];
  onAddProfile: (profile: ParameterSelection_ProfileFragment) => void;
  onDeselectProfile: (profile: ParameterSelection_ProfileFragment) => void;
  onAddParam: (paramInput: ParameterToAddInput) => void;
  onRemove: (paramId: string) => void;
  onInfo: (param: ParameterSelection_RequestableParameterFragment) => void;
  processing: boolean;
  flipParamNames: boolean;
};

const Profiles: React.FC<ProfilesProps> = ({
  params,
  requestableByMainId,
  profiles,
  selectedParams,
  selectedProfileIds,
  onAddProfile,
  onDeselectProfile,
  onAddParam,
  onRemove,
  onInfo,
  processing,
  flipParamNames,
}) => {
  const { token } = theme.useToken();
  const [search, setSearch] = useState<string | null>(null);
  const [debouncedSearch] = useDebounce(search, 250);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const filteredProfiles = useMemo(
    () =>
      [...profiles]
        .filter(it => {
          if (debouncedSearch?.length) {
            return it.name.toLowerCase().includes(debouncedSearch.toLowerCase());
          }
          return true;
        })
        .sort((a, b) => deCollator.compare(a.name, b.name)),
    [debouncedSearch, profiles]
  );

  // Shared lookups, computed once per change instead of scanned inside every card.
  const showLab = useMemo(() => new Set(params.map(p => p.lab.id)).size > 1, [params]);
  const selectedIds = useMemo(() => new Set(selectedParams.map(sp => sp.id)), [selectedParams]);
  const disabledIds = useMemo(() => new Set(selectedParams.flatMap(sp => sp.withoutParameterIds)), [selectedParams]);

  const toggleExpand = useCallback((id: string) => setExpandedId(cur => (cur === id ? null : id)), []);

  // Only show the spinner if processing lasts a moment — avoids a flicker on quick single-param toggles.
  const [showSpinner, setShowSpinner] = useState(false);
  useEffect(() => {
    if (!processing) {
      setShowSpinner(false);
      return;
    }
    const timeout = setTimeout(() => setShowSpinner(true), 400);
    return () => clearTimeout(timeout);
  }, [processing]);

  const styles = useMemo(
    () => ({
      root: css`
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;
      `,
      body: css`
        position: relative;
        flex: 1;
        min-height: 0;
        display: flex;
        flex-direction: column;
      `,
      list: css`
        flex: 1;
        min-height: 0;
        overflow-y: auto;
        display: flex;
        flex-direction: column;
        gap: ${token.marginXS}px;
        padding-top: ${token.paddingSM}px;
        padding-right: ${token.paddingXXS}px;
      `,
      overlay: css`
        position: absolute;
        inset: 0;
        display: flex;
        align-items: flex-start;
        justify-content: center;
        padding-top: ${token.paddingXL}px;
        background: ${token.colorBgContainer}99;
        z-index: 1;
      `,
    }),
    [token]
  );

  return (
    <div className={styles.root}>
      <Input
        allowClear
        placeholder="Suche nach Bezeichnung"
        value={search ?? ''}
        onChange={e => setSearch(e.target.value)}
        prefix={<SearchOutlined />}
      />
      <div className={styles.body}>
        {showSpinner && (
          <div className={styles.overlay}>
            <Spin />
          </div>
        )}
        <div className={styles.list}>
          {filteredProfiles.length ? (
            filteredProfiles.map(profile => (
              <MemoProfileCard
                key={profile.id}
                profile={profile}
                requestableByMainId={requestableByMainId}
                selectedIds={selectedIds}
                disabledIds={disabledIds}
                showLab={showLab}
                expanded={expandedId === profile.id}
                selected={selectedProfileIds.includes(profile.id)}
                flipParamNames={flipParamNames}
                highlight={debouncedSearch ?? ''}
                onToggleExpand={toggleExpand}
                onAddProfile={onAddProfile}
                onDeselectProfile={onDeselectProfile}
                onAddParam={onAddParam}
                onRemove={onRemove}
                onInfo={onInfo}
              />
            ))
          ) : (
            <Empty
              description={debouncedSearch?.length ? 'Keine Profile gefunden' : 'Es wurden noch keine Profile angelegt'}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export const MemoProfiles = memo(Profiles);
